import {Loaded, isLoaded} from 'Core/models/Async';
import {updateMasterPolicyQuote} from 'Quoting/ducks/createQuote';
import {VALUE_NOT_AVAILABLE, isDefined, parsePath} from 'Quoting/utils/utilities';
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {benefitOptions} from '../QuoteForm/Plan/MappingPaths';
import {MappingState} from '../shared/CheckboxInputGrid';

export type GetQuoteFieldValueFn = <T = string>(path: string | undefined) => T | undefined;
export type UpdateQuoteFieldValueFn = (path: string | undefined, newValue: any) => void;
export type UpdateQuoteFieldValuesFn = (fields: {path: string; newValue: any}[]) => void;

export const useQuoteField = () => {
  const quote = useSelector((state: ReducerState) => state.quoteForm.quote);
  const dispatch = useDispatch();

  const getQuoteFieldValue = useCallback<GetQuoteFieldValueFn>(
    (path: string | undefined) =>
      isLoaded(quote) && isDefined(path) ? (quote.Data?.getIn(parsePath(path)) as any) : VALUE_NOT_AVAILABLE,
    [quote]
  );

  const updateQuoteFieldValue = useCallback<UpdateQuoteFieldValueFn>(
    (path: string | undefined, newValue: any) => {
      if (isLoaded(quote) && isDefined(path)) {
        dispatch(updateMasterPolicyQuote(new Loaded(quote.Data?.setIn(parsePath(path), newValue))));
      }
    },
    [quote]
  );

  const updateQuoteFieldValues = useCallback<UpdateQuoteFieldValuesFn>(
    (fields: {path: string; newValue: any}[]) => {
      if (isLoaded(quote) && isDefined(fields)) {
        const updatedQuote = quote.Data.withMutations(quoteData => {
          fields.forEach(({path, newValue}) => {
            quoteData?.setIn(parsePath(path), newValue);
          });
        });
        dispatch(updateMasterPolicyQuote(new Loaded(updatedQuote)));
      }
    },
    [quote]
  );

  const getBenefitLabelMapping = (
    benefitName: string,
    parentPath: string,
    keyFlag?: {type: string; flag: string; section?: string}
  ) => {
    return (benefitOptions[benefitName] &&
      (Object.entries(benefitOptions[benefitName]).map(([k, v], index) => ({
        label: `${v}`,
        path: keyFlag ? `${parentPath}.${keyFlag.type}[${index}].${keyFlag.flag}` : `${parentPath}.${k}`,
        section: keyFlag?.section,
      })) as unknown)) as MappingState[];
  };

  return isLoaded(quote)
    ? {
        getQuoteFieldValue,
        getBenefitLabelMapping,
        updateQuoteFieldValue,
        updateQuoteFieldValues,
        quote,
      }
    : {
        getQuoteFieldValue,
        getBenefitLabelMapping,
        updateQuoteFieldValue,
        updateQuoteFieldValues,
      };
};
